import { wordpressCartGiveawayEvents } from '@dni-analysis'
import { useDialog } from '@dni-hooks/useDialog'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { Collapse } from '@pets-ui/Collapse/Collapse'
import { HStack, type HStackProps } from '@pets-ui/HStack/HStack'
import { ArrowDownSmall, Gift } from '@pets-ui/icons'
import { Check } from '@pets-ui/icons'
import { Image } from '@pets-ui/Image/Image'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { scrollToElementAndExecute } from '@utils/scrollToElementAndExecute'
import { sumOfArray } from '@utils/sumOfArray'
import { memo, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import cartUiI18nInstance from '../i18n/i18nConfig'
import { DIALOG_SELECT_GIVEAWAY, type DialogSelectGiveawayContentProps } from './DialogSelectGiveaway'
import { GiveawaySelectTitle } from './GiveawaySelectTitle'
interface SelectedGiveawayItemProps {
  name?: string
  quantity: number
  attributes?: Record<string, string>
  shippingConflictMessage?: string
  onAdjustShipping?: () => void
}

const SelectedGiveawayItem = (props: SelectedGiveawayItemProps) => {
  const { name, quantity, attributes, shippingConflictMessage, onAdjustShipping } = props
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })
  return (
    <VStack gap={1} paddingRight={1}>
      <HStack alignItems="center">
        <HStack gap={1} flex={1}>
          <Check fontSize={20} color={colors.red600} />
          <Box pl={0.5}>
            <Text variant="subtitle">{name}</Text>
          </Box>
        </HStack>
        <Text variant="caption" color={colors.grey600}>
          x
          {quantity}
        </Text>
      </HStack>
      <Box pl={1}>
        <Text variant="caption" color={colors.grey600} rootStyle={styles.selectedGiveawayAttributes}>{Object.values(attributes || {}).join(', ')}</Text>
      </Box>
      {shippingConflictMessage && (
        <HStack gap={2} alignItems="center" rootStyle={styles.selectedGiveawayAttributes}>
          <Text variant="caption" color={colors.red600}>{shippingConflictMessage}</Text>
          {onAdjustShipping && (
            <Button
              variant="text"
              color="blue"
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                onAdjustShipping()
              }}
            >
              {t('cartUi.goToAdjustShipping', { defaultValue: '前往調整' })}
            </Button>
          )}
        </HStack>
      )}
    </VStack>
  )
}

const styles = stylex.create({
  root: {
    borderColor: colors.grey300,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
    gap: 4,
    overflow: 'hidden',
    alignItems: 'center',
    cursor: 'pointer',
    flexWrap: 'nowrap',
  },
  container: {
    position: 'relative',
    height: '20px',
    width: '20px',
  },
  selectedTitleBarBase: {
    gap: 4,
    justifyContent: 'space-between',
    width: '100%',
  },
  selectedGiveawayAttributes: {
    paddingLeft: 24,
  },
  selectedGiveawaysContainer: {
    padding: 6,
    overflow: 'hidden',
    width: '100%',
  },
  isUnSelectedActive: {
    boxShadow: '0 0 8px 0 rgba(255, 83, 83, 1)',
  },
  errorHighlight: {
    borderColor: colors.red600,
  },
  collapseArrow: {
    transform: 'rotate(180deg)',
  },
})

export interface GiveawaySelectProps extends DialogSelectGiveawayContentProps {
  rootRef?: HStackProps['rootRef']
  errorMessage?: string
  errorHighlight?: boolean
  conflictProductIds?: Set<string>
  shippingConflictMessage?: string
  onAdjustShipping?: () => void
  onClick?: () => void
}

const GiveawaySelectComponent = (props: GiveawaySelectProps) => {
  const {
    couponCriterionId,
    rootRef,
    title,
    options = [],
    accumulatedGiftSelectionCount,
    isRepeatable,
    isGiveAll,
    isStockPartiallyNotEnough,
    defaultGiveawayValues,
    onClick,
    onAdjustShipping,
    onClose,
    onSubmit,
    errorHighlight,
    errorMessage,
    conflictProductIds,
    shippingConflictMessage,
    instanceKey,
    ...rest
  } = props
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })
  const totalSelected = useMemo(() => sumOfArray(Object.values(defaultGiveawayValues || {}), (acc, cur) => acc + cur.quantity), [defaultGiveawayValues])
  const { open: isOpenDialog, couponCriterionId: openDialogCouponCriterionId, instanceKey: openDialogInstanceKey } = useDialog<DialogSelectGiveawayContentProps>(DIALOG_SELECT_GIVEAWAY)
  const selectedGiveaways = useMemo(() => Object.values(defaultGiveawayValues || {})?.filter(giveaway => giveaway.quantity > 0), [defaultGiveawayValues])
  // 同 criterion 展開多張卡片時（兌換券一票一卡），需再比對 instanceKey 才只亮起被開啟的那張；
  // 一般贈品兩側 instanceKey 皆為 undefined，行為不變。
  const isUnSelectedActive = isOpenDialog && couponCriterionId === openDialogCouponCriterionId && instanceKey === openDialogInstanceKey
  const isMobile = useResponsive('mobile')
  const selectedGiveawaysWithName = useMemo(() => selectedGiveaways.map((giveaway) => {
    const option = options.find(option => option.id === giveaway.product_id)
    return {
      ...giveaway,
      name: option?.name,
      attributes: option?.attributes,
      shippingConflictMessage: conflictProductIds?.has(giveaway.product_id) ? shippingConflictMessage : undefined,
    }
  }), [selectedGiveaways, options, conflictProductIds, shippingConflictMessage])
  const hasShippingConflict = useMemo(() => selectedGiveawaysWithName.some(giveaway => giveaway.shippingConflictMessage), [selectedGiveawaysWithName])

  const isReadOnly = !!defaultGiveawayValues?.[options[0]?.id ?? 0]?.isReadyOnly
  const isAllSelected = totalSelected === accumulatedGiftSelectionCount || isReadOnly

  const [isCollapse, setIsCollapse] = useState(false)

  const internalRef = useRef<HTMLDivElement>(null)
  const { sendGiftBlockSelectBtnUnselected, sendGiftBlockSelectBtnPartial, sendGiftBlockSelectBtnAllSelected } = wordpressCartGiveawayEvents()
  const handleSendGiftBlockSelectBtn = useCallback(() => {
    const isPartialSelected = totalSelected > 0 && !isAllSelected
    if (isAllSelected) {
      sendGiftBlockSelectBtnUnselected()
    }
    else if (isPartialSelected) {
      sendGiftBlockSelectBtnPartial()
    }
    else {
      sendGiftBlockSelectBtnAllSelected()
    }
  }, [isAllSelected, sendGiftBlockSelectBtnAllSelected, sendGiftBlockSelectBtnPartial, sendGiftBlockSelectBtnUnselected, totalSelected])

  const handleClick = useCallback(() => {
    handleSendGiftBlockSelectBtn()
    scrollToElementAndExecute(internalRef.current, {
      isMobile,
      onComplete: onClick,
    })
  }, [handleSendGiftBlockSelectBtn, isMobile, onClick])

  return (
    <VStack
      rootRef={internalRef}
      rootStyle={[styles.root, isUnSelectedActive ? styles.isUnSelectedActive : undefined, (errorHighlight || hasShippingConflict) ? styles.errorHighlight : undefined]}
      onClick={handleClick}
      data-criterion-id={couponCriterionId}
      {...rest}
    >
      <HStack rootStyle={[styles.selectedTitleBarBase]}>
        <GiveawaySelectTitle
          title={title}
          isAllSelected={isAllSelected}
          totalSelected={totalSelected}
        />
        <VStack gap={2} rootStyle={styles.selectedGiveawaysContainer}>
          {selectedGiveawaysWithName.length > 0
            ? (
              <>
                {selectedGiveawaysWithName.slice(0, 2).map(giveaway => (
                  <SelectedGiveawayItem
                    key={giveaway.product_id}
                    name={giveaway.name}
                    quantity={giveaway.quantity}
                    attributes={giveaway.attributes}
                    shippingConflictMessage={giveaway.shippingConflictMessage}
                    onAdjustShipping={onAdjustShipping}
                  />
                ))}
                {selectedGiveawaysWithName.length > 2 && (
                  <Collapse in={isCollapse || hasShippingConflict}>
                    {selectedGiveawaysWithName.length > 2 && selectedGiveawaysWithName.slice(2).map(giveaway => (
                      <SelectedGiveawayItem
                        key={giveaway.product_id}
                        name={giveaway.name}
                        quantity={giveaway.quantity}
                        attributes={giveaway.attributes}
                        shippingConflictMessage={giveaway.shippingConflictMessage}
                        onAdjustShipping={onAdjustShipping}
                      />
                    ))}
                  </Collapse>
                )}
                {selectedGiveawaysWithName.length > 2 && !hasShippingConflict && (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsCollapse(prev => !prev)
                    }}
                    size="small"
                    color="grey"
                    variant="ghost"
                    display="flex"
                    gap={1}
                    backgroundColor={colors.grey50}
                  >
                    {isCollapse ? t('cartUi.hiddenGiveaway', { defaultValue: '收合贈品' }) : t('cartUi.showGiveaway', { count: selectedGiveawaysWithName.slice(2).length, defaultValue: '展開贈品（{{count}}）' })}
                    <ArrowDownSmall rootStyle={isCollapse ? styles.collapseArrow : undefined} />
                  </Button>
                )}
              </>
            )
            : (
              <HStack gap={1} overflow="hidden" flexWrap="nowrap" width="100%">
                {options.map(option => (
                  <Box key={option.id} borderRadius={4} overflow="hidden" borderWidth={1} borderColor={colors.grey300} borderStyle="solid" minWidth={32} minHeight={32}>
                    <Image
                      src={option.thumbnail || ''}
                      alt={option.name}
                      width={32}
                      height={32}
                      fallback={<Gift fontSize={32} />}
                    />
                  </Box>
                ))}
              </HStack>
            )}
        </VStack>
      </HStack>
      {errorMessage && (
        <HStack
          gap={2}
          width="100%"
          pl="6px"
          pr="6px"
          pb="6px"
          alignItems="center"
          onClick={(e) => e.stopPropagation()}
        >
          <Text variant="caption" color={colors.red600}>{errorMessage}</Text>
        </HStack>
      )}
    </VStack>
  )
}

export const GiveawaySelect = memo(GiveawaySelectComponent)
